
      import '@folkjs/labs/standalone/folk-shape';
      import '@folkjs/labs/standalone/folk-rope';
      import * as M from '@folkjs/geometry/Matrix3D';
      import { IPointTransform } from '@folkjs/canvas/interfaces/IPointTransform';

      // Define the transform-plane custom element
      class TransformPlane extends HTMLElement {
        [IPointTransform] = true;

        // Get the current transform matrix for the space
        getMatrix() {
          return new DOMMatrix(window.getComputedStyle(this).transform);
        }

        // IPointTransform implementation
        mapPointFromParent(point) {
          return M.projectPointOntoPlane(point, this.getMatrix());
        }

        mapVectorFromParent(vector) {
          // For vectors, we need to transform the direction properly in 3D space
          // We'll do this by transforming two points and finding the difference

          // Create a point at the origin
          const origin = { x: 0, y: 0 };

          // Create a point representing the vector from origin
          const vectorPoint = { x: vector.x, y: vector.y };

          // Transform both points using the same projection method as mapPointFromParent
          const transformedOrigin = M.projectPointOntoPlane(origin, this.getMatrix());
          const transformedVectorPoint = M.projectPointOntoPlane(vectorPoint, this.getMatrix());

          // The transformed vector is the difference between these points
          return {
            x: transformedVectorPoint.x - transformedOrigin.x,
            y: transformedVectorPoint.y - transformedOrigin.y,
          };
        }

        mapPointToParent(point) {
          return M.projectPointFromPlane(point, this.getMatrix());
        }

        mapVectorToParent(vector) {
          const matrix = this.getMatrix();
          // Inverse transform for vectors (ignoring translation)
          // Calculate determinant for inverse
          const det = matrix.a * matrix.d - matrix.b * matrix.c;
          if (Math.abs(det) < 1e-6) return vector; // Avoid division by near-zero

          const invDet = 1 / det;
          return {
            x: (vector.x * matrix.d - vector.y * matrix.c) * invDet,
            y: (-vector.x * matrix.b + vector.y * matrix.a) * invDet,
          };
        }
      }

      // Register the custom element
      customElements.define('transform-plane', TransformPlane);

      // Create ropes for each shape pair
      const shapePairs = [
        { source: 'source1', target: 'target1' },
        { source: 'source2', target: 'target2' },
        { source: 'source3', target: 'target3' },
      ];

      // Create ropes
      const ropes = shapePairs.map((pair) => {
        const rope = document.createElement('folk-rope');
        document.body.appendChild(rope);
        return {
          rope,
          sourceId: pair.source,
          targetId: pair.target,
        };
      });

      // Get plane elements
      const frontPlane = document.getElementById('space1');
      const backPlane = document.getElementById('space2');
      document.querySelectorAll('folk-shape').forEach((shape) => {
        shape.refreshTransformStack();
      });

      // Animation parameters
      const frontPlaneAnimation = {
        rotationX: 0,
        rotationY: 0,
        rotationZ: 0,
        scaleX: 1,
        scaleY: 1,
        translateX: 0,
        translateY: 0,
        translateZ: 0,
      };

      const backPlaneAnimation = {
        rotationX: 0,
        rotationY: 0,
        rotationZ: 0,
        scaleX: 1,
        scaleY: 1,
        translateX: 0,
        translateY: 0,
        translateZ: -1000,
      };

      // Animation speeds (now using integer values)
      const speeds = {
        frontRotationX: 5,
        frontRotationY: 8,
        frontRotationZ: 4,
        frontScaleX: 3,
        frontScaleY: 4,
        frontTranslateX: 12,
        frontTranslateY: 9,

        backRotationX: 7,
        backRotationY: 4,
        backRotationZ: 6,
        backScaleX: 5,
        backScaleY: 3,
        backTranslateX: 10,
        backTranslateY: 11,
      };

      // Animation limits
      const limits = {
        rotation: 0.8, // Increased rotation limit for less parallel movement
        scale: 0.4, // Increased scale range
        translate: 15, // Reduced from 30 to 15 for less movement
      };

      // Animation control
      let isAnimating = true;
      let lastTimestamp = 0;

      // Animation loop
      function animate(timestamp) {
        if (isAnimating) {
          updatePlaneTransforms(timestamp);
        }
        // Always update rope connections, even when animation is paused
        updateRopeConnections();
        requestAnimationFrame(animate);
      }

      // Start animation
      requestAnimationFrame(animate);

      // Grid toggle functionality
      const toggleGridBtn = document.getElementById('toggle-grid');
      let gridVisible = false;

      toggleGridBtn.addEventListener('click', () => {
        gridVisible = !gridVisible;

        // Toggle grid visibility
        if (gridVisible) {
          frontPlane.style.backgroundImage = 'radial-gradient(circle, rgba(0, 0, 0, 0.2) 2px, transparent 1px)';
          backPlane.style.backgroundImage = 'radial-gradient(circle, rgba(0, 0, 0, 0.2) 2px, transparent 1px)';
        } else {
          frontPlane.style.backgroundImage = 'none';
          backPlane.style.backgroundImage = 'none';
        }
      });

      // Animation toggle functionality
      const toggleAnimationBtn = document.getElementById('toggle-animation');

      toggleAnimationBtn.addEventListener('click', () => {
        isAnimating = !isAnimating;
        toggleAnimationBtn.textContent = isAnimating ? 'Pause' : 'Play';
      });

      // Update plane transforms
      function updatePlaneTransforms(timestamp) {
        // Divide timestamp by a large value to work with integer speeds
        const time = timestamp / 1000;

        // Update front plane animation parameters
        frontPlaneAnimation.rotationX = Math.sin((time * speeds.frontRotationX) / 10) * limits.rotation;
        frontPlaneAnimation.rotationY = Math.sin((time * speeds.frontRotationY) / 10) * limits.rotation;
        frontPlaneAnimation.rotationZ = Math.sin((time * speeds.frontRotationZ) / 10) * limits.rotation;
        // Make planes tend to be smaller by using 0.8 instead of 1 as base scale
        frontPlaneAnimation.scaleX = 0.8 + Math.sin((time * speeds.frontScaleX) / 10) * limits.scale;
        frontPlaneAnimation.scaleY = 0.8 + Math.sin((time * speeds.frontScaleY) / 10) * limits.scale;
        frontPlaneAnimation.translateX = Math.sin((time * speeds.frontTranslateX) / 10) * limits.translate;
        frontPlaneAnimation.translateY = Math.sin((time * speeds.frontTranslateY) / 10) * limits.translate;

        // Update back plane animation parameters with different phase to ensure non-parallel movement
        backPlaneAnimation.rotationX = Math.sin((time * speeds.backRotationX) / 10 + 0.7) * limits.rotation;
        backPlaneAnimation.rotationY = Math.sin((time * speeds.backRotationY) / 10 + 0.5) * limits.rotation;
        backPlaneAnimation.rotationZ = Math.sin((time * speeds.backRotationZ) / 10 + 0.3) * limits.rotation;
        // Make planes tend to be smaller
        backPlaneAnimation.scaleX = 0.3 + Math.sin((time * speeds.backScaleX) / 10) * limits.scale;
        backPlaneAnimation.scaleY = 0.3 + Math.sin((time * speeds.backScaleY) / 10) * limits.scale;
        backPlaneAnimation.translateX = Math.sin((time * speeds.backTranslateX) / 10) * limits.translate;
        backPlaneAnimation.translateY = Math.sin((time * speeds.backTranslateY) / 10) * limits.translate;

        // Create front plane matrix
        const frontMatrix = new DOMMatrix();
        frontMatrix.translateSelf(
          window.innerWidth / 2 + frontPlaneAnimation.translateX,
          window.innerHeight / 2 + frontPlaneAnimation.translateY,
          frontPlaneAnimation.translateZ,
        );
        frontMatrix.rotateSelf(
          frontPlaneAnimation.rotationX * (180 / Math.PI),
          frontPlaneAnimation.rotationY * (180 / Math.PI),
          frontPlaneAnimation.rotationZ * (180 / Math.PI),
        );
        frontMatrix.scaleSelf(frontPlaneAnimation.scaleX, frontPlaneAnimation.scaleY, 1);
        frontMatrix.translateSelf(-window.innerWidth / 2, -window.innerHeight / 2, 0);

        // Create back plane matrix
        const backMatrix = new DOMMatrix();
        backMatrix.translateSelf(
          window.innerWidth / 2 + backPlaneAnimation.translateX,
          window.innerHeight / 2 + backPlaneAnimation.translateY,
          backPlaneAnimation.translateZ,
        );
        backMatrix.rotateSelf(
          backPlaneAnimation.rotationX * (180 / Math.PI),
          backPlaneAnimation.rotationY * (180 / Math.PI),
          backPlaneAnimation.rotationZ * (180 / Math.PI),
        );
        backMatrix.scaleSelf(backPlaneAnimation.scaleX, backPlaneAnimation.scaleY, 1);
        backMatrix.translateSelf(-window.innerWidth / 2, -window.innerHeight / 2, 0);

        // Apply matrices directly
        frontPlane.style.transform = frontMatrix.toString();
        backPlane.style.transform = backMatrix.toString();
      }

      // Update rope connections
      function updateRopeConnections() {
        ropes.forEach(({ rope, sourceId, targetId }) => {
          const sourceElement = document.getElementById(sourceId);
          const targetElement = document.getElementById(targetId);

          if (sourceElement && targetElement) {
            const sourcePos = {
              x: sourceElement.x + sourceElement.width / 2,
              y: sourceElement.y + sourceElement.height / 2,
            };
            const targetPos = {
              x: targetElement.x + targetElement.width / 2,
              y: targetElement.y + targetElement.height / 2,
            };

            // Use the element directly instead of trying to access through Symbol
            const frontTransform = frontPlane;
            const backTransform = backPlane;

            // Project points to parent (global) space
            const projectedSourcePoint = frontTransform.mapPointToParent(sourcePos);
            const projectedTargetPoint = backTransform.mapPointToParent(targetPos);

            // Create zero-width/height rects at the projected points
            const sourceRectForRope = {
              x: projectedSourcePoint.x,
              y: projectedSourcePoint.y,
              width: 0,
              height: 0,
            };

            const targetRectForRope = {
              x: projectedTargetPoint.x,
              y: projectedTargetPoint.y,
              width: 0,
              height: 0,
            };

            // Update rope connection points
            rope.sourceRect = sourceRectForRope;
            rope.targetRect = targetRectForRope;
          }
        });
      }
    